import { IconAuthor, IconCalendar } from '@vs-shop/ui/icons';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { clsxm } from '@vs-shop/utils';
import { coalesceNonEmpty } from '@vs-shop/domain/string/coalesceNonEmpty';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import Image from '@vs-shop/image-link/Image';
import Link from '@vs-shop/image-link/Link';
import { IntlDate } from '../../../intl/Intl';

import type { HomepageArticle } from '../../../../../effect/schema/openapi/backend';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

export default function HomepageArticlePreview({ article }: { article: HomepageArticle }) {
  const publishedAt = article.publishedAt;
  const author = article.authors[0] ?? null;

  const imagePathAbstract = article.thumbnailImageAbstract ?? null;
  const imagePath =
    imagePathAbstract !== null
      ? resolveImageUrl(imagePathAbstract, ImageFilter.blog_article_thumb_homepage)
      : null;

  const firstTag = article.tags[0] ?? null;

  const altTitle = coalesceNonEmpty(article.metaTitle, article.title) ?? '';

  return (
    <div className="relative grid h-full">
      <Link href={article.routerUrl} title={altTitle}>
        <div className="flex h-60 w-full items-center justify-center bg-background-inverted-intense pho:h-[366px] des:h-[533px]">
          {imagePath !== null ? (
            <Image
              src={imagePath}
              alt={altTitle}
              width={710}
              height={533}
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageMissingPlaceholder />
          )}
        </div>
      </Link>

      {firstTag !== null && (
        <div
          className={clsxm(
            'absolute top-6 left-0 bg-warning-intense px-3 py-2 text-xs tracking-widest text-foreground uppercase des:top-12 des:px-5 des:py-3 des:text-sm' /*, firstTag.color !== null ? `bg-${firstTag.color}` : '' */
          )}
        >
          {firstTag.name}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col px-4 py-4 backdrop-blur backdrop-brightness-50 des:px-8 des:py-6">
        <div className="text-xs font-semibold tracking-widest text-foreground-inverted/50 uppercase">
          {article.section.name ?? ''}
        </div>
        <Link
          href={article.routerUrl}
          title={altTitle}
          className={clsxm(
            'font-heading',
            'line-clamp-2 pt-1 text-base text-foreground-inverted hover:underline lap:pt-3 lap:text-xl des:text-[26px] @[700px]:w-4/5'
          )}
        >
          {article.title}
        </Link>

        <div
          className={clsxm(
            'hidden gap-3 pt-6 font-medium tracking-widest text-foreground-inverted/60 uppercase lap:flex des:gap-5'
          )}
        >
          <div className="flex items-center gap-2">
            <span>
              <IconCalendar className="size-4 text-primary" />
            </span>
            <span className="text-xs/4 whitespace-nowrap">
              <span className="@[750px]:hidden">
                <IntlDate value={publishedAt} month="2-digit" />
              </span>
              <span className="hidden @[750px]:block">
                <IntlDate value={publishedAt} month="long" />
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span>
              <IconAuthor className="size-4 text-primary" />
            </span>
            {author !== null && <span className="text-xs/4 whitespace-nowrap">{author.name}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
